import { RefObject, useCallback, useEffect, useState } from 'react'

import { timeout } from '@/utils'

import type { IAnnotationEngine } from '../core/AnnotationEngine'
import { HTMLAnchorFragment } from '../types'

interface UseAnchorPositionOptions {
  fragment: HTMLAnchorFragment | null
  containerRef: RefObject<HTMLElement | null>
  shadowRootRef: RefObject<ShadowRoot | null>
  engine: IAnnotationEngine | null
  scrollContainerRef?: RefObject<HTMLElement | null>
  enabled?: boolean
  offset?: {
    top?: number
    left?: number
  }
}

interface Position {
  top: number
  left: number
}

export const useAnchorPosition = ({
  fragment,
  containerRef,
  shadowRootRef,
  engine,
  scrollContainerRef,
  enabled = true,
  offset,
}: UseAnchorPositionOptions): {
  position: Position | null
  recalculate: () => void
} => {
  const [position, setPosition] = useState<Position | null>(null)

  const calculatePosition = useCallback(async (): Promise<Position | null> => {
    const containerEl = containerRef.current
    const shadowRoot = shadowRootRef.current

    if (!enabled || !fragment || !containerEl || !shadowRoot || !engine) {
      return null
    }

    try {
      const domLocator = engine.getDomLocator()

      const node = domLocator.resolveXPath(fragment.startPath)

      if (!node) {
        return null
      }

      let nodeLength = 0
      if (node.nodeType === Node.TEXT_NODE) {
        nodeLength = (node as Text).data.length
      } else {
        // fallback for non-text nodes, though your startPath should usually point to text()
        nodeLength = node.textContent?.length ?? 0
      }

      let startOffset = fragment.startOffset
      let endOffset = fragment.endOffset

      if (startOffset > nodeLength) {
        startOffset = nodeLength
      }

      if (endOffset > nodeLength) {
        endOffset = nodeLength
      }

      // Optional: ensure non-negative and start <= end
      startOffset = Math.max(0, startOffset)
      endOffset = Math.max(startOffset, endOffset)

      await timeout(200)

      const ownerDoc = node.ownerDocument || document
      const range = ownerDoc.createRange()
      range.setStart(node, startOffset)
      range.setEnd(node, endOffset)

      const rangeRect = range.getBoundingClientRect()

      if (rangeRect.width === 0 && rangeRect.height === 0) {
        return null
      }

      const containerRect = containerEl.getBoundingClientRect()

      const calculatedPosition = {
        top: rangeRect.top - containerRect.top + (offset?.top ?? 0),
        left: rangeRect.left - containerRect.left + (offset?.left ?? 0),
      }

      return calculatedPosition
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to calculate anchor position:', error)
      return null
    }
  }, [enabled, fragment, containerRef, shadowRootRef, engine, offset])

  const recalculate = useCallback(() => {
    calculatePosition().then(setPosition)
  }, [calculatePosition])

  useEffect(() => {
    if (!enabled || !fragment || !engine) {
      setPosition(null)
      return
    }

    const updatePosition = async () => {
      const newPosition = await calculatePosition()
      setPosition(newPosition)
    }

    const findScrollableParent = (
      element: HTMLElement | null
    ): HTMLElement | null => {
      if (!element) return null

      const { overflow, overflowY } = globalThis.getComputedStyle(element)
      const isScrollable =
        overflow === 'auto' ||
        overflow === 'scroll' ||
        overflowY === 'auto' ||
        overflowY === 'scroll'

      if (isScrollable && element.scrollHeight > element.clientHeight) {
        return element
      }

      return findScrollableParent(element.parentElement)
    }

    updatePosition()

    let scrollContainer: HTMLElement | null = null

    if (scrollContainerRef?.current) {
      scrollContainer = scrollContainerRef.current
    } else {
      scrollContainer = findScrollableParent(containerRef.current)
    }

    const handleRecalculate = () => {
      updatePosition()
    }

    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', handleRecalculate, {
        passive: true,
      })
    }

    window.addEventListener('resize', handleRecalculate)

    return () => {
      if (scrollContainer) {
        scrollContainer.removeEventListener('scroll', handleRecalculate)
      }
      window.removeEventListener('resize', handleRecalculate)
    }
  }, [
    enabled,
    fragment,
    engine,
    shadowRootRef,
    containerRef,
    offset,
    scrollContainerRef,
    calculatePosition,
  ])

  return {
    position,
    recalculate,
  }
}
